import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, total } = useCart();
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60]"
            onClick={() => setOpen(false)}
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-surface border-l border-white/10 z-[70] flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="font-display text-xl uppercase tracking-wide">Your Haul</h2>
              <button data-testid="cart-close-button" onClick={() => setOpen(false)} className="text-zinc-400 hover:text-kinetic transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {items.length === 0 && (
                <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 pt-10 text-center" data-testid="cart-empty-message">
                  Cart is empty. Go build something.
                </p>
              )}
              {items.map((i) => (
                <div key={i.key} className="flex gap-4 border border-white/10 p-3 bg-card" data-testid={`cart-item-${i.key}`}>
                  <img src={i.image} alt={i.name} className="w-20 h-24 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm uppercase tracking-wide truncate">{i.name}</p>
                    <p className="font-mono text-[11px] text-zinc-500 uppercase mt-1">{i.size} / {i.color}</p>
                    {i.customization?.text && (
                      <p className="font-mono text-[11px] text-kinetic uppercase mt-1">
                        {i.customization.method} · {i.customization.placement} · “{i.customization.text}”
                      </p>
                    )}
                    {i.customization?.artwork_path && (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${i.customization.artwork_path}`}
                        alt="Custom artwork"
                        data-testid={`cart-artwork-${i.key}`}
                        className="mt-2 w-10 h-10 object-contain border border-kinetic/40 bg-black"
                      />
                    )}
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-white/10">
                        <button data-testid={`cart-qty-minus-${i.key}`} onClick={() => updateQty(i.key, i.qty - 1)} className="p-1.5 text-zinc-400 hover:text-kinetic"><Minus size={13} /></button>
                        <span className="px-3 font-mono text-xs">{i.qty}</span>
                        <button data-testid={`cart-qty-plus-${i.key}`} onClick={() => updateQty(i.key, i.qty + 1)} className="p-1.5 text-zinc-400 hover:text-kinetic"><Plus size={13} /></button>
                      </div>
                      <span className="font-mono text-sm text-zinc-200">${(i.unit_price * i.qty).toFixed(2)}</span>
                    </div>
                  </div>
                  <button data-testid={`cart-remove-${i.key}`} onClick={() => removeItem(i.key)} className="self-start text-zinc-600 hover:text-red-500 transition-colors">
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
            {items.length > 0 && (
              <div className="p-6 border-t border-white/10 space-y-4">
                <div className="flex justify-between font-mono text-sm uppercase tracking-widest">
                  <span className="text-zinc-400">Subtotal</span>
                  <span data-testid="cart-subtotal" className="text-white">${total.toFixed(2)}</span>
                </div>
                <button
                  data-testid="cart-checkout-button"
                  onClick={() => { setOpen(false); navigate("/checkout"); }}
                  className="btn-kinetic w-full"
                >
                  Checkout
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
